import type { Call } from '@entities/call';
import type { UserPhoneNumber } from '@entities/user';
import type { BaseUsecase } from '@usecases/base-usecase';

export type WrapUpTimeStartedRequest = {
  Id: string;
  status: string;
  callType: string;
  mandatoryTaggingRequired?: boolean;
};

export interface WrapUpTimeCallRepository {
  getCurrentCall(): Promise<Call>;
}

// Optional dependency used only to read the mandatory-tagging flag; kept narrow
// so the gate can be wired in without touching unrelated call sites.
export interface WrapUpTimeUserRepository {
  getOutboundUserPhoneNumber(): Promise<UserPhoneNumber>;
}

export interface WrapUpTimeStartedEventPublisher {
  publish(call: WrapUpTimeStartedRequest): Promise<void>;
}
export interface WrapUpTimeStartedCallUsecase extends BaseUsecase<void> {
  run(): Promise<Call | void>;
}

export class WrapUpTimeStartedUseCase implements WrapUpTimeStartedCallUsecase {
  constructor(
    private repository: WrapUpTimeCallRepository,
    private publisher: WrapUpTimeStartedEventPublisher,
    private userRepository?: WrapUpTimeUserRepository,
  ) {}

  async run(): Promise<void> {
    const call = await this.repository.getCurrentCall();
    if (!call) return;

    console.log('getCurrentCall - ' + call);

    const wrapUpTimeStartedRequest: WrapUpTimeStartedRequest = {
      Id: call.id,
      status: call.status,
      callType: call.type,
      mandatoryTaggingRequired: await this.isMandatoryTaggingRequired(),
    };
    await this.publisher.publish(wrapUpTimeStartedRequest);
  }

  // Fail-open: if the user repo is absent or the lookup throws, treat tagging as
  // not mandatory so a transient outage can never trap an agent in wrap-up.
  private async isMandatoryTaggingRequired(): Promise<boolean> {
    if (!this.userRepository) return false;
    try {
      const number = await this.userRepository.getOutboundUserPhoneNumber();
      return Boolean(number?.mandatory_tagging_activated);
    } catch {
      return false;
    }
  }
}
